import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import EspacioCard from '../components/EspacioCard';
import { getEspacios } from './Locales';

/* Iconos de las ventajas (trazo fino, mismo lenguaje que el resto) */
const VENTAJA_ICONS = [
  /* Contratos flexibles */
  <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
    <path d="M14 2v6h6" />
    <path d="M9 15l2 2 4-4" />
  </svg>,
  /* Todo incluido */
  <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <path d="M20 7H4a1 1 0 0 0-1 1v11a1 1 0 0 0 1 1h16a1 1 0 0 0 1-1V8a1 1 0 0 0-1-1z" />
    <path d="M16 7V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v2" />
    <path d="M3 12h18" />
  </svg>,
  /* Zona céntrica */
  <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
    <circle cx="12" cy="10" r="3" />
  </svg>,
  /* Trato directo */
  <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
    <circle cx="9" cy="7" r="4" />
    <path d="M23 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75" />
  </svg>,
];

const ArrowIcon = () => (
  <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <path d="M5 12h14" />
    <path d="m12 5 7 7-7 7" />
  </svg>
);

function Home() {
  const { t, getTranslation } = useLanguage();
  const ventajas = getTranslation('home.ventajas') || [];

  /* 3 espacios destacados, alternando coworking y locales para que se
   * vea la variedad de la oferta y no tres fichas del mismo tipo. */
  const destacados = useMemo(() => {
    const espacios = getEspacios();
    const coworking = espacios.filter((e) => e.isCoworking);
    const locales = espacios.filter((e) => !e.isCoworking);
    const mixed = [];
    for (let i = 0; i < Math.max(locales.length, coworking.length); i += 1) {
      if (coworking[i]) mixed.push(coworking[i]);
      if (locales[i]) mixed.push(locales[i]);
    }
    return mixed.slice(0, 3);
  }, []);

  return (
    <>
      <section className="hero">
        <div className="hero-overlay" />
        <div className="hero-content">
          <h1 className="hero-title" aria-label={t('home.title')}>
            <span className="hero-title-text hero-title-text--reveal">
              {t('home.title')}
            </span>
          </h1>
          <p className="hero-subtitle">
            {t('home.subtitle')}
          </p>
          <div className="hero-actions">
            <Link to="/locales" className="hero-btn hero-btn--primary">
              {t('home.ctaPrimary')}
            </Link>
            <Link to="/contacto" className="hero-btn">
              {t('home.cta')}
            </Link>
          </div>
        </div>
        <a href="#espacios-destacados" className="hero-scroll" aria-label={t('home.scroll')}>
          <span className="hero-scroll-mouse" aria-hidden />
          <span>{t('home.scroll')}</span>
        </a>
      </section>

      {/* ---------- Espacios destacados ---------- */}
      <section className="section section--home" id="espacios-destacados">
        <div className="section-inner">
          <span className="section-kicker">{t('home.destacadosKicker')}</span>
          <h2 className="section-title">{t('home.destacadosTitle')}</h2>
          <p className="section-intro">{t('home.destacadosIntro')}</p>

          <div className="espacios-grid">
            {destacados.map((espacio) => (
              <EspacioCard key={espacio.detailSlug} espacio={espacio} />
            ))}
          </div>

          <div className="espacios-more">
            <Link to="/locales" className="btn">
              {t('home.destacadosAll')}
              <ArrowIcon />
            </Link>
          </div>
        </div>
      </section>

      {/* ---------- Por qué Brundin Gestión ---------- */}
      <section className="section section--home">
        <div className="section-inner">
          <span className="section-kicker">{t('home.ventajasKicker')}</span>
          <h2 className="section-title">{t('home.ventajasTitle')}</h2>
          <p className="section-intro">{t('home.ventajasIntro')}</p>

          <div className="ventajas-grid">
            {ventajas.map((v, i) => (
              <div key={v.title} className="ventaja-card">
                <span className="ventaja-card-icon">{VENTAJA_ICONS[i]}</span>
                <h3 className="ventaja-card-title">{v.title}</h3>
                <p className="ventaja-card-text">{v.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ---------- Franja CTA final ---------- */}
      <section className="section section--home section--cta">
        <div className="cta-band">
          <h2 className="cta-band-title">{t('home.ctaBandTitle')}</h2>
          <p className="cta-band-text">{t('home.ctaBandText')}</p>
          <div className="cta-band-actions">
            <a href="tel:+34654866634" className="btn btn--primary">
              {t('home.ctaBandCall')}
            </a>
            <Link to="/contacto" className="btn">
              {t('home.ctaBandContact')}
            </Link>
          </div>
        </div>
      </section>
    </>
  );
}

export default Home;
